import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import {
    ArrowLeft, Gauge, Radar, TrendingUp, Link2, MessageSquare,
    Zap, Play, Trash2, ChevronDown, ChevronUp, Search, Send,
    ExternalLink, Loader2, MailIcon, Cpu, Feather,
} from "lucide-react";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend, Area, AreaChart,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
    { key: "overview", label: "Overview", icon: Gauge },
    { key: "content", label: "Content Engine", icon: Zap },
    { key: "ai", label: "AI Visibility", icon: Radar },
    { key: "gsc", label: "Search Console", icon: TrendingUp },
    { key: "backlinks", label: "Backlinks & PR", icon: Link2 },
    { key: "warroom", label: "War Room", icon: MessageSquare },
];

const BUCKET_META = {
    ownership_story: { label: "Ownership" },
    buyers_guide: { label: "Buyer's Guide" },
    spec_deep_dive: { label: "Spec" },
    market_intel: { label: "Market" },
    real_owner_story: { label: "Real Owner" },
};

export default function VinnyBrain() {
    const [tab, setTab] = useState("overview");
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);

    useEffect(() => {
        api.get("/vinny/status").then(({ data }) => setStatus(data)).catch(() => setStatus({}));
    }, []);

    return (
        <div className="min-h-screen bg-onyx-950 text-white brain-noise">
            <Helmet>
                <title>S.C.A.R.S. Control Center · SRT Finder</title>
            </Helmet>

            {/* Header — dashboard cluster style */}
            <header
                className="border-b border-hellcat/30 relative overflow-hidden"
                style={{ background: "linear-gradient(to right, #050505 0%, #0A0A0A 50%, #050505 100%)" }}
            >
                <div className="brain-scanlines absolute inset-0 pointer-events-none opacity-40" />
                <div className="relative max-w-7xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
                    <button
                        onClick={() => navigate("/admin")}
                        className="btn-ghost px-3 py-1.5 label-caps flex items-center gap-1"
                        style={{ borderRadius: 2 }}
                        data-testid="vinny-back-to-admin"
                    >
                        <ArrowLeft className="h-4 w-4" /> Admin
                    </button>
                    <div className="flex items-center gap-3">
                        <ScarsLogo />
                        <div>
                            <h1
                                className="text-3xl leading-none text-white text-glow-red"
                                style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                            >
                                S.C.A.R.S.
                            </h1>
                            <p className="brain-mono text-[9px] uppercase tracking-[0.25em] text-onyx-500 mt-1">
                                Supercharged Content · Authority Ranking System
                            </p>
                        </div>
                    </div>
                    <div className="text-right label-caps hidden md:block">
                        <div className="brain-mono text-onyx-500">V.1.0</div>
                        <div className="flex items-center gap-1 justify-end mt-0.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-emerald-400 normal-case tracking-normal text-[10px]">ONLINE</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Tab pills */}
            <nav
                className="sticky top-0 z-30 backdrop-blur-md border-b border-onyx-700"
                style={{ background: "rgba(5,5,5,0.95)" }}
            >
                <div className="max-w-7xl mx-auto px-3 md:px-5 py-3 flex gap-2 overflow-x-auto no-scrollbar">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            data-testid={`vinny-tab-${key}`}
                            className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 label-caps border transition-all duration-150 ${
                                tab === key
                                    ? "bg-hellcat border-hellcat text-white glow-red"
                                    : "border-onyx-600 text-onyx-500 hover:text-white hover:border-hellcat"
                            }`}
                            style={{ borderRadius: 2 }}
                        >
                            <Icon className="h-3.5 w-3.5" /> {label}
                        </button>
                    ))}
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 md:px-5 py-8">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={tab}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                    >
                        {tab === "overview" && <OverviewTab status={status} />}
                        {tab === "content" && <ContentEngineTab />}
                        {tab === "ai" && <AiVisibilityTab />}
                        {tab === "gsc" && <SearchConsoleTab />}
                        {tab === "backlinks" && <BacklinksTab />}
                        {tab === "warroom" && <WarRoomTab />}
                    </motion.div>
                </AnimatePresence>
            </main>
        </div>
    );
}

/* ============================== OVERVIEW ============================== */
function OverviewTab({ status }) {
    return (
        <div className="space-y-8">
            {/* Two tach gauges */}
            <div className="grid md:grid-cols-2 gap-5">
                <TachScoreboard
                    title="Google Visibility · SEO"
                    value={status?.gsc?.share ?? 0}
                    unit="%"
                    max={100}
                    metrics={[
                        { label: "Impressions 28D", value: status?.gsc?.impressions ?? "—" },
                        { label: "Queries Ranked", value: status?.gsc?.queries ?? "—" },
                        { label: "Avg Position", value: status?.gsc?.avg_position ?? "—" },
                        { label: "Clicks", value: status?.gsc?.clicks ?? "—" },
                    ]}
                    headline={
                        status?.gsc?.queries
                            ? `Ranking for ${status.gsc.queries} performance Mopar queries`
                            : "Awaiting Google Search Console connection."
                    }
                    testId="scoreboard-seo"
                />
                <TachScoreboard
                    title="AI Share of Voice · AEO"
                    value={status?.probe?.share ?? 0}
                    unit="%"
                    max={100}
                    metrics={[
                        { label: "ChatGPT", value: status?.probe?.chatgpt ?? "—" },
                        { label: "Claude", value: status?.probe?.claude ?? "—" },
                        { label: "Perplexity", value: status?.probe?.perplexity ?? "—" },
                        { label: "Total Cited", value: status?.probe?.cited ?? "—" },
                    ]}
                    headline={
                        status?.probe?.share
                            ? `Cited in ${status.probe.share}% of AI answers about performance Mopars`
                            : "AI probe hasn't run yet — kick it off from AI Visibility."
                    }
                    testId="scoreboard-aeo"
                />
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile label="Queued" value={status?.queued ?? 0} target="Target: 8" />
                <StatTile label="Published" value={status?.published ?? 0} target="~2/day" />
                <StatTile label="Listings" value={status?.listings ?? 0} />
                <StatTile label="Market Snaps" value={status?.market_snapshots ?? 0} target="12 trims" />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                <QuickNavCard idx={1} title="Run Discovery" body="Have Vinny scan for new 95+ opportunities across all four buckets." />
                <QuickNavCard idx={2} title="Check AI Citations" body="Ask ChatGPT, Claude & Perplexity where SRTFinder shows up." />
                <QuickNavCard idx={3} title="War Room" body="Group chat with Vinny and Remi. They see the same data you do." />
            </div>
        </div>
    );
}

/* Tach-style scoreboard: half-circle gauge + big center number */
function TachScoreboard({ title, value, unit, max = 100, metrics, headline, testId }) {
    const numeric = typeof value === "number" ? value : Number(value) || 0;
    const pct = Math.max(0, Math.min(1, numeric / max));
    return (
        <div className="brain-panel-elev brain-scanlines relative overflow-hidden" data-testid={testId}>
            {/* Header strip */}
            <div className="flex items-center justify-between px-5 py-2 border-b border-onyx-700 bg-onyx-900">
                <div className="label-caps text-hellcat">{title}</div>
                <div className="brain-mono text-[10px] text-onyx-500">GAUGE_01</div>
            </div>

            <div className="p-5 flex gap-6 items-center">
                <TachDial pct={pct} display={`${Math.round(numeric)}${unit}`} />
                <div className="grid grid-cols-2 gap-3 flex-1">
                    {metrics.map((m) => (
                        <div key={m.label}>
                            <div className="brain-mono text-2xl md:text-3xl text-white tabular-nums leading-none">
                                {m.value}
                            </div>
                            <div className="label-caps mt-1.5 text-[9px]">{m.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="px-5 pb-4 pt-2 border-t border-onyx-700 text-xs text-onyx-500 leading-relaxed">
                {headline}
            </div>
        </div>
    );
}

function TachDial({ pct, display }) {
    // Half-circle gauge, 180deg sweep from bottom-left to bottom-right
    const size = 148;
    const stroke = 10;
    const r = (size - stroke) / 2;
    const cx = size / 2;
    const cy = size / 2 + 10; // shifted so bottom half is anchor
    // Path arc from left-bottom (180) sweeping to right-bottom (0)
    const startAngle = Math.PI; // 180deg
    const endAngle = 0; // 0deg
    const midAngle = startAngle - pct * Math.PI; // fills counter-clockwise from left
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(midAngle);
    const y2 = cy + r * Math.sin(midAngle);
    const largeArc = pct > 0.5 ? 1 : 0;

    // Motion value for count-up
    const motionVal = useMotionValue(0);
    const rounded = useTransform(motionVal, (v) => `${Math.round(v)}${display.replace(/^-?\d+/, "")}`);
    useEffect(() => {
        const target = Number(display.replace(/[^0-9.-]/g, "")) || 0;
        const controls = animate(motionVal, target, { duration: 0.8, ease: [0.22, 1, 0.36, 1] });
        return controls.stop;
    }, [display, motionVal]);

    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size / 2 + 20 }}>
            <svg width={size} height={size / 2 + 20} viewBox={`0 0 ${size} ${size / 2 + 20}`}>
                {/* Track */}
                <path
                    d={`M ${cx + r * Math.cos(startAngle)} ${cy + r * Math.sin(startAngle)} A ${r} ${r} 0 0 1 ${
                        cx + r * Math.cos(endAngle)
                    } ${cy + r * Math.sin(endAngle)}`}
                    fill="none"
                    stroke="#1F1F1F"
                    strokeWidth={stroke}
                    strokeLinecap="butt"
                />
                {/* Fill */}
                {pct > 0.01 && (
                    <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
                        fill="none"
                        stroke="#E10600"
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        style={{ filter: "drop-shadow(0 0 6px rgba(225,6,0,0.8))" }}
                    />
                )}
                {/* Redline marker */}
                <line
                    x1={cx + (r + 4) * Math.cos(Math.PI * 0.15)}
                    y1={cy + (r + 4) * Math.sin(Math.PI * 0.15)}
                    x2={cx + (r - 6) * Math.cos(Math.PI * 0.15)}
                    y2={cy + (r - 6) * Math.sin(Math.PI * 0.15)}
                    stroke="#E10600"
                    strokeWidth="2"
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                <motion.div
                    className="text-white text-glow-red tabular-nums leading-none"
                    style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontSize: 36 }}
                >
                    {rounded}
                </motion.div>
                <div className="label-caps text-[8px] mt-1">Share of Voice</div>
            </div>
        </div>
    );
}

function StatTile({ label, value, target }) {
    const num = typeof value === "number" ? value : Number(value) || 0;
    const motionVal = useMotionValue(0);
    const rounded = useTransform(motionVal, (v) => Math.round(v));
    useEffect(() => {
        const c = animate(motionVal, num, { duration: 0.7 });
        return c.stop;
    }, [num, motionVal]);
    return (
        <div className="brain-panel-elev p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 h-0.5 w-full bg-hellcat/60" />
            <motion.div className="text-4xl text-white brain-mono tabular-nums font-bold leading-none">
                {typeof value === "number" ? rounded : value}
            </motion.div>
            <div className="label-caps mt-2">{label}</div>
            {target && <div className="brain-mono text-[10px] text-hellcat/80 mt-2">{target}</div>}
        </div>
    );
}

function QuickNavCard({ idx, title, body }) {
    return (
        <div className="brain-panel-elev p-5 hover:border-hellcat transition-all duration-150 relative overflow-hidden">
            <div className="absolute top-3 right-3 brain-mono text-[10px] text-onyx-600">
                CH.{String(idx).padStart(2, "0")}
            </div>
            <h4 className="headline text-lg text-white">{title}</h4>
            <p className="text-xs text-onyx-500 mt-2 leading-relaxed">{body}</p>
        </div>
    );
}

/* ========================= CONTENT ENGINE ========================= */
function ContentEngineTab() {
    const [buckets, setBuckets] = useState([]);
    const [history, setHistory] = useState([]);
    const [opps, setOpps] = useState([]);
    const [published, setPublished] = useState([]);
    const [emailAddr, setEmailAddr] = useState("");
    const [running, setRunning] = useState(false);
    const [showUpNext, setShowUpNext] = useState(true);

    async function loadAll() {
        try {
            const [b, h, o, p, e] = await Promise.all([
                api.get("/vinny/buckets").catch(() => ({ data: [] })),
                api.get("/vinny/buckets/history").catch(() => ({ data: [] })),
                api.get("/vinny/opportunities").catch(() => ({ data: [] })),
                api.get("/blog/posts?status=published&limit=20").catch(() => ({ data: [] })),
                api.get("/vinny/email-settings").catch(() => ({ data: { postal_address: "" } })),
            ]);
            setBuckets(b.data || []);
            setHistory(h.data || []);
            setOpps(o.data || []);
            setPublished(p.data || []);
            setEmailAddr(e.data?.postal_address || "");
        } catch {}
    }
    useEffect(() => { loadAll(); }, []);

    async function runDiscovery() {
        setRunning(true);
        try { await api.post("/vinny/discover"); toast.success("Vinny is scanning."); await loadAll(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setRunning(false); }
    }
    async function deleteOpp(id) {
        try { await api.delete(`/vinny/opportunity/${id}`); setOpps((c) => c.filter((o) => o.id !== id)); }
        catch (err) { toast.error(formatApiError(err)); }
    }
    async function saveEmail() {
        try { await api.post("/vinny/email-settings", { postal_address: emailAddr }); toast.success("Saved."); }
        catch (err) { toast.error(formatApiError(err)); }
    }

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="brain-mono text-[10px] text-hellcat tracking-widest">// CONTENT ENGINE</div>
                    <h2
                        className="text-3xl md:text-4xl text-white text-glow-red"
                        style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    >
                        The Publish Pipe
                    </h2>
                </div>
                <button
                    onClick={runDiscovery}
                    disabled={running}
                    className="btn-red px-5 py-2.5 flex items-center gap-2 label-caps disabled:opacity-50"
                    style={{ borderRadius: 2 }}
                    data-testid="vinny-run-discovery"
                >
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    Run Discovery
                </button>
            </div>

            {/* Bucket competition — vertical fuel gauges */}
            <div>
                <div className="label-caps mb-3">// Bucket Competition · Adaptive Weights</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="bucket-competition">
                    {["ownership_story", "buyers_guide", "spec_deep_dive", "market_intel", "real_owner_story"].map((k) => {
                        const b = buckets.find((x) => x.key === k) || {};
                        const meta = BUCKET_META[k];
                        const w = b.weight != null ? b.weight : (k === "real_owner_story" ? 0 : 0.25);
                        return <BucketGauge key={k} label={meta.label} weight={w} ready={b.ready ?? 0} published={b.published ?? 0} clicks={b.clicks ?? 0} isEvent={k === "real_owner_story"} />;
                    })}
                </div>
            </div>

            {/* Weight drift graph */}
            <div className="brain-panel-elev p-5" data-testid="weight-drift-chart">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="label-caps">// Weight Drift · 8-Day Trailing</div>
                        <h3 className="headline text-xl text-white mt-1">How Vinny reallocates</h3>
                    </div>
                    <div className="brain-mono text-[10px] text-onyx-500">CHART_01</div>
                </div>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={history.length ? history : demoHistory()}>
                            <defs>
                                <linearGradient id="hellcatFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#E10600" stopOpacity="0.4" />
                                    <stop offset="100%" stopColor="#E10600" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#1F1F1F" strokeDasharray="3 3" />
                            <XAxis dataKey="date" stroke="#52525B" fontSize={10} tickLine={false} axisLine={false} />
                            <YAxis
                                stroke="#52525B"
                                fontSize={10}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(v) => `${Math.round(v * 100)}%`}
                            />
                            <RTooltip
                                contentStyle={{
                                    background: "#050505",
                                    border: "1px solid #262626",
                                    borderRadius: 2,
                                    fontSize: 11,
                                    fontFamily: "JetBrains Mono, monospace",
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "Manrope", textTransform: "uppercase", letterSpacing: "0.1em" }} />
                            <Area type="monotone" dataKey="ownership_story" stroke="#E10600" strokeWidth={2} fill="url(#hellcatFill)" />
                            <Line type="monotone" dataKey="buyers_guide" stroke="#FF1E18" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
                            <Line type="monotone" dataKey="spec_deep_dive" stroke="#94A3B8" strokeWidth={1.5} dot={false} />
                            <Line type="monotone" dataKey="market_intel" stroke="#7A0400" strokeWidth={1.5} strokeDasharray="2 3" dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                {history.length === 0 && (
                    <p className="brain-mono text-[10px] text-onyx-500 mt-2 uppercase tracking-widest">
                        // PREVIEW · REAL DATA POPULATES ONCE VINNY RUNS
                    </p>
                )}
            </div>

            {/* Email footer */}
            <div className="brain-panel-elev p-5">
                <div className="label-caps mb-2">// CAN-SPAM footer address</div>
                <h3 className="headline text-lg text-white mb-1">Required by law</h3>
                <p className="text-xs text-onyx-500 mb-4">This appears at the bottom of every marketing email.</p>
                <div className="flex gap-2">
                    <input
                        value={emailAddr}
                        onChange={(e) => setEmailAddr(e.target.value)}
                        placeholder="SRT Finder · PO Box 1701 · Detroit, MI 48226 · USA"
                        className="brain-input flex-1"
                        data-testid="email-footer-input"
                    />
                    <button
                        onClick={saveEmail}
                        className="btn-red px-5 label-caps"
                        style={{ borderRadius: 2 }}
                        data-testid="email-footer-save"
                    >
                        Save
                    </button>
                </div>
            </div>

            {/* Up next */}
            <div className="brain-panel-elev" data-testid="up-next-section">
                <button
                    onClick={() => setShowUpNext((v) => !v)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-onyx-900 transition-colors"
                >
                    <div className="text-left">
                        <div className="label-caps">// UP NEXT · {opps.length} queued</div>
                        <h3 className="headline text-lg text-white mt-1">95+ scored opportunities</h3>
                    </div>
                    {showUpNext ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {showUpNext && (
                    <div className="px-5 pb-5 space-y-2 border-t border-onyx-700 pt-4">
                        {opps.length === 0 ? (
                            <div className="brain-mono text-xs text-onyx-500 py-6 text-center tracking-widest">
                                // QUEUE_EMPTY · RUN_DISCOVERY_TO_STOCK
                            </div>
                        ) : (
                            opps.map((o) => (
                                <div key={o.id} className="flex items-start gap-3 brain-panel p-3">
                                    <div className="brain-mono text-[10px] uppercase tracking-widest px-2 py-1 flex-shrink-0 bg-hellcat/15 text-hellcat border border-hellcat/40">
                                        {BUCKET_META[o.bucket]?.label || o.bucket} · <span className="tabular-nums">{o.score}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-white truncate">{o.topic}</div>
                                        <div className="text-xs text-onyx-500 mt-1">{o.rationale}</div>
                                        <div className="brain-mono text-[10px] text-onyx-600 mt-1 truncate uppercase tracking-widest">
                                            {(o.keywords || []).slice(0, 5).join(" · ")}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => deleteOpp(o.id)}
                                        className="text-onyx-500 hover:text-hellcat flex-shrink-0 transition-colors"
                                        data-testid={`opp-delete-${o.id}`}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Published */}
            <div className="brain-panel-elev p-5" data-testid="published-list">
                <div className="label-caps mb-2">// PUBLISHED BY REMI</div>
                <h3 className="headline text-lg text-white mb-4">The Ladder archive</h3>
                {published.length === 0 ? (
                    <div className="brain-mono text-xs text-onyx-500 tracking-widest">// NOTHING_PUBLISHED_YET</div>
                ) : (
                    <ul className="space-y-2">
                        {published.map((p) => (
                            <li key={p.slug} className="flex items-center justify-between brain-panel p-3 text-sm">
                                <div className="min-w-0">
                                    <div className="font-medium text-white truncate">{p.title}</div>
                                    <div className="brain-mono text-[10px] text-onyx-500 mt-0.5 uppercase tracking-widest">
                                        {BUCKET_META[p.bucket]?.label || p.bucket} · {new Date(p.published_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <a
                                    href={`/the-ladder/${p.slug}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="label-caps text-hellcat hover:underline flex items-center gap-1"
                                >
                                    View <ExternalLink className="h-3 w-3" />
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function BucketGauge({ label, weight, ready, published, clicks, isEvent }) {
    const height = Math.max(6, Math.min(100, weight * 100));
    return (
        <div className="brain-panel-elev p-3 flex flex-col">
            <div className="label-caps text-hellcat mb-2 truncate">{label}</div>
            <div className="flex gap-3">
                <div className="fuel-gauge flex-shrink-0" style={{ width: 22 }}>
                    <motion.div
                        className="fuel-gauge-fill"
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <div className="fuel-gauge-ticks" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="brain-mono text-2xl text-white tabular-nums font-bold leading-none">
                        {Math.round(weight * 100)}
                        <span className="text-onyx-500 text-sm">%</span>
                    </div>
                    <div className="brain-mono text-[9px] text-onyx-500 uppercase tracking-widest mt-1">
                        {isEvent ? "EVENT-DRIVEN" : "ADAPTIVE"}
                    </div>
                </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-1 text-center label-caps text-[8px]">
                <div>
                    <div className="brain-mono text-white text-sm">{ready}/{isEvent ? "∞" : 2}</div>
                    <div>Ready</div>
                </div>
                <div>
                    <div className="brain-mono text-white text-sm">{published}</div>
                    <div>Live</div>
                </div>
                <div>
                    <div className="brain-mono text-white text-sm">{clicks}</div>
                    <div>Clicks</div>
                </div>
            </div>
        </div>
    );
}

function demoHistory() {
    return Array.from({ length: 8 }).map((_, i) => ({
        date: `D${i + 1}`,
        ownership_story: 0.25 + Math.sin(i) * 0.08,
        buyers_guide: 0.25 + Math.cos(i) * 0.06,
        spec_deep_dive: 0.25 - Math.sin(i) * 0.05,
        market_intel: 0.25 - Math.cos(i) * 0.04,
    }));
}

/* ========================= AI VISIBILITY ========================= */
function AiVisibilityTab() {
    const [citations, setCitations] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [running, setRunning] = useState(false);

    async function loadAll() {
        try {
            const [c, q] = await Promise.all([
                api.get("/vinny/citations").catch(() => ({ data: [] })),
                api.get("/vinny/probe-questions").catch(() => ({ data: [] })),
            ]);
            setCitations(c.data || []);
            setQuestions(q.data || []);
        } catch {}
    }
    useEffect(() => { loadAll(); }, []);

    async function runProbe() {
        setRunning(true);
        try { await api.post("/vinny/probe"); toast.success("Probe queued."); await loadAll(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setRunning(false); }
    }

    const grouped = useMemo(() => groupByQuestion(citations), [citations]);
    const shareOfVoice = useMemo(() => calcShareOfVoice(citations), [citations]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="brain-mono text-[10px] text-hellcat tracking-widest">// AI VISIBILITY · AEO</div>
                    <h2
                        className="text-3xl md:text-4xl text-white text-glow-red"
                        style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    >
                        Share of Voice
                    </h2>
                </div>
                <button
                    onClick={runProbe}
                    disabled={running}
                    className="btn-red px-5 py-2.5 flex items-center gap-2 label-caps disabled:opacity-50"
                    style={{ borderRadius: 2 }}
                    data-testid="vinny-run-probe"
                >
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
                    Run Probe
                </button>
            </div>

            {/* Hero — dyno readout */}
            <div
                className="brain-panel-elev brain-scanlines relative overflow-hidden p-8"
                data-testid="share-of-voice"
                style={{ background: "radial-gradient(ellipse at center, rgba(225,6,0,0.12) 0%, transparent 70%), #0A0A0A" }}
            >
                <div className="grid md:grid-cols-2 gap-6 items-center">
                    <div>
                        <div className="label-caps text-hellcat">// DYNO READOUT</div>
                        <div
                            className="text-8xl md:text-9xl text-white text-glow-red tabular-nums leading-none mt-2"
                            style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                        >
                            {shareOfVoice}
                            <span className="text-hellcat">%</span>
                        </div>
                        <div className="label-caps mt-2">Share of Voice · 3 Engines</div>
                    </div>
                    <div className="space-y-3">
                        <p className="text-sm text-onyx-400 leading-relaxed">
                            When people ask AI where to find performance Mopars, this is how often SRTFinder shows up in the answer.
                        </p>
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-onyx-700">
                            <EngineCell name="ChatGPT" list={citations} filter="chatgpt" />
                            <EngineCell name="Claude" list={citations} filter="claude" />
                            <EngineCell name="Perplexity" list={citations} filter="perplexity" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Grouped results */}
            <div className="space-y-3" data-testid="probe-results">
                {Object.keys(grouped).length === 0 ? (
                    <EmptyPanel
                        icon={Radar}
                        title="No probe results yet"
                        body="Run the probe to see where SRTFinder shows up across ChatGPT, Claude, and Perplexity."
                    />
                ) : (
                    Object.entries(grouped).map(([question, results]) => (
                        <div key={question} className="brain-panel-elev p-4">
                            <div className="text-sm font-semibold text-white">{question}</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {results.map((r, i) => (
                                    <span
                                        key={i}
                                        className={`brain-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                                            r.mentioned
                                                ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                                                : "bg-onyx-800 border-onyx-600 text-onyx-500"
                                        }`}
                                    >
                                        {r.engine} {r.mentioned ? "✓" : "×"}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="brain-panel-elev p-5" data-testid="probe-questions">
                <div className="label-caps mb-2">// ACTIVE PROBE QUESTIONS</div>
                <h3 className="headline text-lg text-white mb-4">Questions Vinny asks the engines</h3>
                {questions.length === 0 ? (
                    <div className="brain-mono text-xs text-onyx-500 tracking-widest">
                        // VINNY_WILL_SEED_8_QUESTIONS_ON_FIRST_RUN
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {questions.map((q) => (
                            <li key={q.id} className="flex items-center justify-between brain-panel p-3 text-sm">
                                <span className="flex-1 text-white">{q.text}</span>
                                <div className="flex gap-2 items-center flex-shrink-0">
                                    <StateChip state={q.state} />
                                    <span className="brain-mono text-[10px] text-onyx-500 tabular-nums">
                                        {q.mentions ?? 0}/{q.checks ?? 0}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function EngineCell({ name, list, filter }) {
    const total = list.filter((r) => (r.engine || "").toLowerCase().includes(filter)).length;
    const cited = list.filter((r) => (r.engine || "").toLowerCase().includes(filter) && r.mentioned).length;
    return (
        <div>
            <div className="label-caps text-[9px]">{name}</div>
            <div className="brain-mono text-lg text-white tabular-nums mt-1">
                {cited}
                <span className="text-onyx-500 text-xs">/{total}</span>
            </div>
        </div>
    );
}

function StateChip({ state }) {
    const map = {
        won: { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", label: "WON" },
        stalled: { color: "bg-hellcat/15 text-hellcat border-hellcat/40", label: "STALLED" },
        gap: { color: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40", label: "GAP" },
        active: { color: "bg-onyx-800 text-onyx-400 border-onyx-600", label: "ACTIVE" },
    };
    const m = map[state] || map.active;
    return <span className={`brain-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${m.color}`}>{m.label}</span>;
}
function groupByQuestion(list) {
    const g = {};
    for (const r of list) {
        const key = r.question || r.prompt || "—";
        if (!g[key]) g[key] = [];
        g[key].push(r);
    }
    return g;
}
function calcShareOfVoice(list) {
    if (!list.length) return 0;
    const cited = list.filter((r) => r.mentioned).length;
    return Math.round((cited / list.length) * 100);
}

/* ========================= SEARCH CONSOLE ========================= */
function SearchConsoleTab() {
    const [gsc, setGsc] = useState(null);
    const [syncing, setSyncing] = useState(false);
    useEffect(() => {
        api.get("/vinny/search-console").then(({ data }) => setGsc(data)).catch(() => setGsc(null));
    }, []);
    async function sync() {
        setSyncing(true);
        try {
            await api.post("/vinny/gsc-sync");
            toast.success("GSC synced.");
            const { data } = await api.get("/vinny/search-console");
            setGsc(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSyncing(false); }
    }
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="brain-mono text-[10px] text-hellcat tracking-widest">// GOOGLE SEARCH CONSOLE</div>
                    <h2
                        className="text-3xl md:text-4xl text-white text-glow-red"
                        style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    >
                        Search Signals
                    </h2>
                </div>
                <button
                    onClick={sync}
                    disabled={syncing}
                    className="btn-ghost px-5 py-2 label-caps flex items-center gap-2 disabled:opacity-50"
                    style={{ borderRadius: 2 }}
                    data-testid="vinny-gsc-sync"
                >
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                    Sync 28D
                </button>
            </div>

            {!gsc || !gsc.connected ? (
                <EmptyPanel
                    icon={TrendingUp}
                    title="GSC not connected"
                    body="Add GOOGLE_GSC_SA_JSON and set GSC_PROPERTY to your verified property. Once wired, 28-day queries and per-post results flow in automatically."
                />
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile label="Impressions 28D" value={gsc.impressions ?? 0} />
                        <StatTile label="Clicks" value={gsc.clicks ?? 0} />
                        <StatTile label="Queries" value={gsc.queries?.length ?? 0} />
                        <StatTile label="Avg Position" value={gsc.avg_position ?? "—"} />
                    </div>
                    <div className="brain-panel-elev p-5">
                        <div className="label-caps mb-2">// TOP QUERIES</div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm brain-mono tabular-nums">
                                <thead className="text-left label-caps border-b border-onyx-700">
                                    <tr>
                                        <th className="py-2 pr-4">Query</th>
                                        <th>Clicks</th>
                                        <th>Impressions</th>
                                        <th>Position</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(gsc.queries || []).map((q, i) => (
                                        <tr key={i} className="border-b border-onyx-800 hover:bg-onyx-900 transition-colors">
                                            <td className="py-2 pr-4 text-white font-sans">{q.query}</td>
                                            <td className="text-onyx-300">{q.clicks}</td>
                                            <td className="text-onyx-300">{q.impressions}</td>
                                            <td className="text-hellcat font-bold">{Number(q.position).toFixed(1)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

/* ========================= BACKLINKS & PR ========================= */
function BacklinksTab() {
    const [items, setItems] = useState([]);
    const [prAngles, setPrAngles] = useState([]);
    const [scanning, setScanning] = useState(false);
    const [showPR, setShowPR] = useState(false);

    async function loadAll() {
        const [b, p] = await Promise.all([
            api.get("/vinny/backlinks").catch(() => ({ data: [] })),
            api.get("/vinny/pr-angles").catch(() => ({ data: [] })),
        ]);
        setItems(b.data || []);
        setPrAngles(p.data || []);
    }
    useEffect(() => { loadAll(); }, []);

    async function scan() {
        setScanning(true);
        try { await api.post("/vinny/backlinks/scan"); toast.success("Radar sweeping."); await loadAll(); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setScanning(false); }
    }
    async function markStatus(id, status) {
        try { await api.post(`/vinny/backlinks/${id}/status`, { status }); await loadAll(); }
        catch (err) { toast.error(formatApiError(err)); }
    }
    async function verify(id) {
        try {
            const { data } = await api.post(`/vinny/backlinks/${id}/verify`);
            toast.info(`Verified: ${data.result || "checked"}`);
            await loadAll();
        } catch (err) { toast.error(formatApiError(err)); }
    }
    async function generatePR() {
        try { await api.post("/vinny/pr-angles/generate"); toast.success("Fresh PR angles drafted."); await loadAll(); }
        catch (err) { toast.error(formatApiError(err)); }
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="brain-mono text-[10px] text-hellcat tracking-widest">// BACKLINKS &amp; PR</div>
                    <h2
                        className="text-3xl md:text-4xl text-white text-glow-red"
                        style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    >
                        Authority Engine
                    </h2>
                </div>
                <button
                    onClick={scan}
                    disabled={scanning}
                    className="btn-red px-5 py-2.5 flex items-center gap-2 label-caps disabled:opacity-50"
                    style={{ borderRadius: 2 }}
                    data-testid="vinny-backlinks-scan"
                >
                    {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    Scan
                </button>
            </div>

            {items.length === 0 ? (
                <EmptyPanel
                    icon={Link2}
                    title="No opportunities yet"
                    body="Radar pulls unlinked mentions and resource-page targets via Tavily. Add TAVILY_API_KEY to activate."
                />
            ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="backlink-opportunities">
                    {items.map((o) => (
                        <div key={o.id} className="brain-panel-elev p-4">
                            <div className="flex items-center justify-between">
                                <span className="label-caps text-hellcat">{o.type}</span>
                                <span className="brain-mono text-[10px] text-onyx-500 tabular-nums">
                                    SCORE {String(o.score).padStart(2, "0")}
                                </span>
                            </div>
                            <h4 className="headline text-lg text-white mt-2">{o.title}</h4>
                            <p className="text-xs text-onyx-500 mt-1 leading-relaxed">{o.summary}</p>
                            <div className="mt-3 flex flex-wrap items-center gap-2 label-caps">
                                {o.contact_email && (
                                    <a
                                        href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(o.contact_email)}&su=${encodeURIComponent(o.subject || "")}&body=${encodeURIComponent(o.email_draft || "")}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-ghost px-3 py-1 flex items-center gap-1 text-[10px]"
                                        style={{ borderRadius: 2 }}
                                    >
                                        <MailIcon className="h-3 w-3" /> Gmail
                                    </a>
                                )}
                                <button
                                    onClick={() => markStatus(o.id, "won")}
                                    className="px-3 py-1 text-[10px] bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/40"
                                    style={{ borderRadius: 2 }}
                                >
                                    Got the link
                                </button>
                                <button
                                    onClick={() => verify(o.id)}
                                    className="btn-ghost px-3 py-1 text-[10px]"
                                    style={{ borderRadius: 2 }}
                                >
                                    Verify
                                </button>
                                <button
                                    onClick={() => markStatus(o.id, "dismissed")}
                                    className="btn-ghost px-3 py-1 text-[10px] text-onyx-500"
                                    style={{ borderRadius: 2 }}
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* PR Drafts */}
            <div className="brain-panel-elev">
                <button onClick={() => setShowPR((v) => !v)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-onyx-900 transition-colors">
                    <div className="text-left">
                        <div className="label-caps">// PR DRAFTS · {prAngles.length}</div>
                        <h3 className="headline text-lg text-white mt-1">Linkable-asset ideas</h3>
                    </div>
                    {showPR ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {showPR && (
                    <div className="px-5 pb-5 space-y-3 border-t border-onyx-700 pt-4">
                        <button
                            onClick={generatePR}
                            className="btn-ghost px-4 py-2 label-caps"
                            style={{ borderRadius: 2 }}
                        >
                            Generate fresh angles
                        </button>
                        {prAngles.length === 0 && (
                            <div className="brain-mono text-xs text-onyx-500 tracking-widest">// NO ANGLES YET</div>
                        )}
                        {prAngles.map((p) => (
                            <div key={p.id} className="brain-panel p-3">
                                <div className="text-sm font-semibold text-white">{p.headline}</div>
                                <div className="text-xs text-onyx-500 mt-1">{p.rationale}</div>
                                <div className="brain-mono text-[10px] text-hellcat mt-2 uppercase tracking-widest">
                                    Target: {(p.outlets || []).join(" · ")}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ============================ WAR ROOM ============================ */
function WarRoomTab() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const sessionId = useRef(`warroom_${Date.now()}`);

    useEffect(() => {
        api.get("/vinny/chat").then(({ data }) => setMessages(data || [])).catch(() => {});
    }, []);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    async function send() {
        const text = input.trim();
        if (!text) return;
        setMessages((m) => [...m, { role: "owner", text, ts: Date.now() }]);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/vinny/chat", { message: text, session_id: sessionId.current });
            if (data?.vinny) setMessages((m) => [...m, { role: "vinny", text: data.vinny, ts: Date.now() }]);
            if (data?.remi) setMessages((m) => [...m, { role: "remi", text: data.remi, ts: Date.now() }]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="brain-mono text-[10px] text-hellcat tracking-widest">// WAR ROOM · GROUP CHAT</div>
                    <h2
                        className="text-3xl md:text-4xl text-white text-glow-red"
                        style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    >
                        Vinny + Remi
                    </h2>
                </div>
                <div className="flex items-center gap-3 label-caps">
                    <PersonaChip icon={Cpu} name="Vinny · Strategy" />
                    <PersonaChip icon={Feather} name="Remi · Voice" />
                </div>
            </div>

            <div
                ref={scrollRef}
                className="h-[62vh] overflow-y-auto brain-panel-elev p-4 space-y-4"
                data-testid="warroom-messages"
            >
                {messages.length === 0 && (
                    <div className="text-center py-20 text-onyx-500">
                        <MessageSquare className="h-10 w-10 mx-auto mb-3 text-hellcat/60" />
                        <div className="brain-mono text-xs uppercase tracking-widest text-hellcat mb-2">
                            // TERMINAL_READY
                        </div>
                        <p className="text-sm">Group chat with Vinny (strategy) and Remi (voice). Both see your live data.</p>
                        <p className="text-xs mt-3 brain-mono text-onyx-600">
                            $ &gt; "What's our biggest AI-citation gap right now?"
                        </p>
                    </div>
                )}
                {messages.map((m, i) => (
                    <MessageBubble key={i} m={m} />
                ))}
                {sending && (
                    <div className="flex items-center gap-2 brain-mono text-xs text-onyx-500 uppercase tracking-widest">
                        <Loader2 className="h-4 w-4 animate-spin text-hellcat" /> VINNY &amp; REMI ARE THINKING...
                    </div>
                )}
            </div>

            <div className="flex gap-2">
                <div className="brain-mono text-hellcat px-3 flex items-center text-lg select-none">$&gt;</div>
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !sending && send()}
                    placeholder="Ask Vinny and Remi anything about the engine..."
                    className="brain-input flex-1 brain-mono"
                    data-testid="warroom-input"
                />
                <button
                    onClick={send}
                    disabled={sending || !input.trim()}
                    className="btn-red px-5 disabled:opacity-50"
                    style={{ borderRadius: 2 }}
                    data-testid="warroom-send"
                >
                    <Send className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

function PersonaChip({ icon: Icon, name }) {
    return (
        <span className="flex items-center gap-1.5 brain-panel px-2 py-1 border-hellcat/30">
            <Icon className="h-3 w-3 text-hellcat" />
            <span className="brain-mono text-[10px]">{name}</span>
        </span>
    );
}

function MessageBubble({ m }) {
    if (m.role === "owner") {
        return (
            <div className="flex justify-end">
                <div className="max-w-[75%] bg-white text-onyx-950 px-4 py-2 text-sm font-medium" style={{ borderRadius: 2 }}>
                    {m.text}
                </div>
            </div>
        );
    }
    // Vinny — strategist, monospace, hard borders, uppercase
    if (m.role === "vinny") {
        return (
            <div className="flex justify-start gap-3">
                <div className="h-9 w-9 flex-shrink-0 flex items-center justify-center bg-hellcat text-white brain-mono font-bold shadow-red-glow" style={{ borderRadius: 2 }}>
                    <Cpu className="h-5 w-5" />
                </div>
                <div className="max-w-[75%] flex-1">
                    <div className="brain-mono text-[10px] uppercase tracking-widest text-hellcat mb-1">
                        // VINNY · STRATEGIST
                    </div>
                    <div className="border-l-4 border-hellcat brain-panel p-3 text-sm brain-mono text-onyx-100 whitespace-pre-wrap leading-relaxed">
                        {m.text}
                    </div>
                </div>
            </div>
        );
    }
    // Remi — writer, serif, softer border, editorial
    return (
        <div className="flex justify-start gap-3">
            <div className="h-9 w-9 flex-shrink-0 flex items-center justify-center bg-onyx-800 border border-hellcat/40 text-hellcat" style={{ borderRadius: 999 }}>
                <Feather className="h-4 w-4" />
            </div>
            <div className="max-w-[75%] flex-1">
                <div className="brain-mono text-[10px] uppercase tracking-widest text-onyx-400 mb-1">
                    remi · voice
                </div>
                <div className="border-l-2 border-onyx-500 pl-4 py-2 text-[15px] text-white leading-relaxed" style={{ fontFamily: "'DM Serif Display', serif" }}>
                    {m.text}
                </div>
            </div>
        </div>
    );
}

/* ============================== SHARED ============================== */
function EmptyPanel({ icon: Icon, title, body }) {
    return (
        <div className="brain-panel border-dashed p-10 text-center relative overflow-hidden" style={{ borderStyle: "dashed" }}>
            <div className="brain-scanlines absolute inset-0 pointer-events-none opacity-30" />
            <div className="relative">
                <Icon className="h-10 w-10 mx-auto text-hellcat mb-3 text-glow-red" />
                <div className="brain-mono text-[10px] uppercase tracking-widest text-hellcat mb-2">// STANDBY</div>
                <h3 className="headline text-xl text-white">{title}</h3>
                <p className="text-sm text-onyx-500 max-w-md mx-auto mt-2 leading-relaxed">{body}</p>
            </div>
        </div>
    );
}

function ScarsLogo() {
    return (
        <svg width="46" height="46" viewBox="0 0 60 60" className="drop-shadow-[0_0_12px_rgba(225,6,0,0.6)]">
            <defs>
                <linearGradient id="scarsGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FF1E18" />
                    <stop offset="100%" stopColor="#0A0A0A" />
                </linearGradient>
                <radialGradient id="scarsInter" cx="0.5" cy="0.5" r="0.5">
                    <stop offset="0%" stopColor="#FF1E18" />
                    <stop offset="100%" stopColor="#E10600" stopOpacity="0" />
                </radialGradient>
            </defs>
            <rect x="8" y="14" width="44" height="32" rx="2" fill="url(#scarsGrad)" stroke="#E10600" strokeWidth="1.5" />
            {[16, 22, 28, 34, 40, 46].map((x) => (
                <line key={x} x1={x} y1="18" x2={x} y2="42" stroke="#000" strokeOpacity="0.5" strokeWidth="1.5" />
            ))}
            <circle cx="30" cy="30" r="5" fill="url(#scarsInter)" />
            <circle cx="30" cy="30" r="2.5" fill="#E10600" />
        </svg>
    );
}
